import React from 'react';
import { useTranslation } from 'react-i18next';
import { InputLabel, MenuItem, Select, ListItemText, Checkbox, Chip, FormControl, SelectChangeEvent } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { chipTypes, fieldTypes } from '@/helpers/constants';
import { getFieldCursorType } from '@/helpers/utils';
import useAppLanguage from '@/hooks/useAppLanguage';
import { useFieldSelection } from '@/hooks/useFieldSelection';
import useWarningField from '@/hooks/useWarningField';
import { InputVariants } from '@/types';
import ArrayChipLogic from '../ArrayChips/ArrayChipLogic';
import MandatoryLabel from '../MandatoryLabel/MandatoryLabel';
import UniversalChip from '../UniversalChip/UniversalChip';
import HighlightText from '../../HighlightText/HighlightText';

const useStyles = makeStyles(() => ({
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    padding: '4px 0',
  },
  chip: {
    margin: 0,
  },
  InputLabel: {
    backgroundColor: 'white',
  },
}));

type MultiSelectionProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any;
  formControlClass: string;
  id: string;
  label: React.ReactNode;
  value: any;
  handleChange: (event: SelectChangeEvent<{ value: unknown }>) => void;
  open?: boolean;
  maxChip?: number;
  isMandatory?: boolean;
  disabled: boolean;
  handleDeleteItem?: (item: any) => void;
  invalid?: boolean;
  variant?: InputVariants;
  isOnlyPlants?: boolean;
  isModelSeries?: boolean;
  isPlants?: boolean;
  language?: string;
  onOpen?: () => void;
  onClose?: () => void;
  handleMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
  fieldPath?: string[];
  withoutTranslations?: boolean;
  size?: 'small' | 'medium';
};

const MultiSelection: React.FC<MultiSelectionProps> = (props) => {
  const { options = [], formControlClass, id, label, value, handleChange = () => { }, open = undefined, maxChip = 2,
    isMandatory = false, disabled, handleDeleteItem, invalid, variant = 'outlined', isOnlyPlants = false, isModelSeries = false,
    isPlants = false, language, onOpen = () => null, onClose = () => null, handleMouseDown, fieldPath, withoutTranslations = false,
    size } = props;
  const inputLabel = isMandatory ? (<>{label}<MandatoryLabel /></>) : label;
  const classes = useStyles();
  const lang = language || useAppLanguage();
  const [tab = '', fieldName = '', fieldSecondaryName = ''] = fieldPath || [];
  const { handleFieldSelection, isFieldSelected, isSelectionModeOn } = useFieldSelection({ tab, fieldName, fieldSecondaryName });
  const warning = useWarningField(fieldPath, fieldTypes.MULTI_SELECTION) || invalid || isFieldSelected;

  const buildLabel = (item) => {
    if (isOnlyPlants) return options.find((option) => option.wkzNumber === item)?.plantName ?? item;

    if (isPlants) return options.find((option) => option.wkzNumber === item.wkzNumber)?.plantName ?? item.plantName ?? item.wkzNumber;

    if (isModelSeries) return options.find((option) => option === item) ?? item;

    if (withoutTranslations) return item;

    return options[item]?.[lang] ?? item;
  };

  const buildChipData = (selected) => (isPlants ? selected.map((item) => item.plantName) : selected);

  const handleOnChange = (e: SelectChangeEvent<{ value: unknown }>) => {
    if (isFieldSelected) {
      handleFieldSelection(e);
    }

    handleChange(e);
  };

  return (
    <FormControl
      error={warning}
      variant={variant}
      className={`${formControlClass} ${warning ? 'validation-warning' : ''} ${warning ? 'ValidationIndicatorField' : ''}`}
      onClick={isSelectionModeOn ? handleFieldSelection : undefined}
      sx={{ '& *': getFieldCursorType(isSelectionModeOn) }}
    >
      {!!label && (
        <InputLabel id={`${id}-label`} className={`${warning ? '' : classes.InputLabel}`}>
          {inputLabel}
        </InputLabel>
      )}
      <Select
        key={`${id}-label`}
        labelId={`${id}-label`}
        id={id}
        data-testid={id}
        inputProps={{
          id: `${id}-select-input`,
          'data-testid': `${id}-select-input`,
        }}
        value={value}
        multiple
        onChange={handleOnChange}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        onMouseDown={handleMouseDown}
        disabled={disabled}
        size={size}
        renderValue={(selected: any) => (
          <div className={classes.chips}>
            {disabled
              ? (
                <UniversalChip
                  id={id}
                  fieldPath={fieldPath}
                  chipType={chipTypes.READONLY}

                  arrayStrings={selected.map((item) => (isOnlyPlants
                    ? (options.find((option) => option.wkzNumber === item)?.plantName ?? item)
                    : isModelSeries
                      ? item
                      : (options[item]?.[lang] ?? item)))}
                />
              )
              : (
                <>
                  {(isModelSeries || isPlants)
                    ? (
                      <ArrayChipLogic
                        id={id}
                        disabled={false}
                        detailTitle={typeof label === 'string' ? label : undefined}
                        fieldPath={fieldPath}
                        chipData={buildChipData(selected)}
                        handleDelete={handleDeleteItem}
                        maxChip={maxChip}
                      />
                    )
                    : (
                      selected.map((item) => (
                        <Chip
                          clickable
                          key={item}
                          label={(
                            <HighlightText
                              fieldId={id}
                              fieldPath={fieldPath ?? []}
                              fieldType={fieldTypes.MULTI_SELECTION}
                            >
                              {buildLabel(item)}
                            </HighlightText>
                          )}
                          disabled={disabled}
                          onMouseDown={(event) => {
                            event.stopPropagation();
                          }}
                          size={size}
                          className={classes.chip}
                          onClick={handleDeleteItem ? () => handleDeleteItem(item) : undefined}
                          onDelete={handleDeleteItem ? () => handleDeleteItem(item) : undefined}
                        />
                      ))
                    )}
                </>
              )}
          </div>
        )}
      >
        {isOnlyPlants
          && (options.map((option) => (
            <MenuItem key={options.indexOf(option)} value={option}>
              <Checkbox
                color="primary"
                checked={value && value.indexOf(option.wkzNumber) > -1}
              />
              <ListItemText primary={option.plantName} />
            </MenuItem>
          ))
          )}
        {isPlants
          && (options.map((option, index) => (

            <MenuItem id={`option-${option.wkzNumber}`} key={index} value={option}>
              <Checkbox
                color="primary"
                checked={value && value.find((val) => val?.wkzNumber === option.wkzNumber) !== undefined}
              />
              <ListItemText primary={option.plantName} />
            </MenuItem>
          ))
          )}
        {isModelSeries
          && (options.map((option) => (
            <MenuItem id={`option-${option}`} key={options.indexOf(option)} value={option}>
              <Checkbox
                color="primary"
                checked={value && value.indexOf(option) > -1}
              />
              <ListItemText primary={option} />
            </MenuItem>
          ))
          )}
        {!isModelSeries && !isOnlyPlants && !isPlants
          && (
            (withoutTranslations ? options : Object.keys(options)).map((option) => (
              <MenuItem key={option} value={option}>
                <Checkbox
                  color="primary"
                  checked={(value && value.indexOf(option) > -1)}
                />
                <ListItemText primary={withoutTranslations ? option : options[option][lang]} />
              </MenuItem>
            )))}
      </Select>
    </FormControl>
  );
};

export default MultiSelection;
